import { ArrowDown, ArrowUp } from '@ui/components/icons';
import { TextTooltip } from '@ui/components/TextTooltip';
import { useIsMounted } from '@ui/hooks/useIsMounted';
import { formatNumber, formatWithThousandSeparators } from '@ui/lib/bignumber';
import { Field } from '@ui/lib/park/ui/field';
import { Icon } from '@ui/lib/park/ui/icon';
import { RadioButtonGroup } from '@ui/lib/park/ui/radio-button-group';
import { Text } from '@ui/lib/park/ui/text';
import { Nullable, PossibleBigNumber } from '@ui/types/utility';
import { getEstPnl } from '@ui/utils/trade/getEstPnl';
import { getTakeProfitStopLossPrice } from '@ui/utils/trade/getTakeProfitStopLossPrice';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Divider,
  Flex,
  Grid,
  HStack,
  HstackProps,
  styled,
} from 'styled-system/jsx';

import { useDebounceTpSlInput } from '../../hooks/useDebounceTpSlInput';
import { useIsTradePageContainerDesktop } from '../../hooks/useIsTradePageContainerDesktop';
import { defaultTpSlValue } from '../../store/defaultTpSlValue';
import { TpSlValueState } from '../../types';
import { getPositiveTakeProfitState } from '../../utils/getPositiveTakeProfitState';
import {
  getTpQuickMaxRatio,
  getTpQuickRatios,
} from '../../utils/getTpQuickRatios';
import {
  getStopLossPriceErrorMessage,
  SlValidationResult,
} from '../../utils/validateStoppLossPrice';
import {
  getTakeProfitPriceErrorMessage,
  TpValidationResult,
} from '../../utils/validateTakeProfitPrice';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const isSameTpSlValue = (a: TpSlValueState, b: TpSlValueState) =>
  a.priceValue === b.priceValue &&
  Object.is(a.pnlPercentage, b.pnlPercentage) &&
  Object.is(a.deltaPnl, b.deltaPnl) &&
  a.isPnlPercentage === b.isPnlPercentage &&
  a.isLong === b.isLong;

type Props = HstackProps & {
  tp: TpSlValueState;
  setTp: (value: React.SetStateAction<TpSlValueState>) => void;
  sl: TpSlValueState;
  setSl: (value: React.SetStateAction<TpSlValueState>) => void;
  entryPrice: Nullable<number>;
  leverage: number;
  collateralUsd: PossibleBigNumber;
  isLong: boolean;
  priceDisplayDecimals: number;
  maxTpRatios: Nullable<number>;
  tpValidation: TpValidationResult | undefined;
  slValidation: SlValidationResult | undefined;
  spreadPercentage: number | undefined;
  defaultTpEnabled: boolean | undefined;
  /**
   * Always render the full form, skipping the responsive collapsed summary.
   * Used in the edit modal so it doesn't flash the collapsed row first
   * (the container breakpoint resolves to `null` until measured post-paint).
   */
  forceExpanded?: boolean;
  /** V2 trade-panel variant: readout rows + preset chips only (no manual inputs) */
  quickPanel?: boolean;
};

const useCursorPersistence = (value: string) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorRef = useRef<number | null>(null);

  const handleInput = (
    e: React.FormEvent<HTMLInputElement>,
    callback: (val: string) => void,
  ) => {
    const input = e.currentTarget;
    const originalValue = input.value;
    const selectionStart = input.selectionStart || 0;
    const rawValue = originalValue.replaceAll(',', '');

    if (!/^\d*\.?\d*$/.test(rawValue)) return;

    // Store logic position (count non-comma chars before cursor)
    const valueBeforeCursor = originalValue.slice(0, selectionStart);
    const nonCommaCount = valueBeforeCursor.replace(/,/g, '').length;
    cursorRef.current = nonCommaCount;

    callback(rawValue);
  };

  useIsomorphicLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      if (inputRef.current && cursorRef.current !== null) {
        const input = inputRef.current;
        const newValue = input.value;

        // Restore cursor position based on non-comma count
        let newPos = 0;
        let currentNonComma = 0;
        for (let i = 0; i < newValue.length; i++) {
          if (currentNonComma === cursorRef.current) {
            newPos = i;
            break;
          }
          if (newValue[i] !== ',') {
            currentNonComma++;
          }
          // Edge case: if we reach the end
          if (i === newValue.length - 1) {
            newPos = newValue.length;
          }
        }

        input.setSelectionRange(newPos, newPos);
        cursorRef.current = null;
      }
    }
  }, [value]);

  return { inputRef, handleInput };
};

export const TPSLInputSection = (p: Props) => {
  const { t } = useTranslation();
  const {
    tp,
    setTp,
    sl,
    setSl,
    entryPrice,
    leverage,
    collateralUsd,
    isLong,
    priceDisplayDecimals,
    maxTpRatios,
    spreadPercentage,
    tpValidation: tpValidationParam,
    slValidation: slValidationParam,
    defaultTpEnabled,
    forceExpanded,
    quickPanel,
    ...props
  } = p;

  const tpValidation = tpValidationParam || {
    valid: true,
    error: undefined,
  };
  const slValidation = slValidationParam || {
    valid: true,
    error: undefined,
  };
  const [priceUpdatePaused, setPriceUpdatePaused] = useState(false);
  const { value: isContainerDesktop } = useIsTradePageContainerDesktop();
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine if input is PnL percentage based on state
  const [isTpPnlInput, setIsTpPnlInput] = useState(false);
  const [isSlPnlInput, setIsSlPnlInput] = useState(false);

  // Temporary state for PnL input value
  const [tpPnlInputValue, setTpPnlInputValue] = useState('');
  const [slPnlInputValue, setSlPnlInputValue] = useState('');

  // Update PnL input value when tp.pnlPercentage changes externally (e.g. from radio buttons)
  useEffect(() => {
    if (!isTpPnlInput && tp.pnlPercentage) {
      setTpPnlInputValue(
        formatNumber(tp.pnlPercentage * 100, {
          displayDecimals: 0,
          isTrimTrailingZero: true,
        }),
      );
    }
  }, [tp.pnlPercentage, isTpPnlInput]);

  useEffect(() => {
    if (!isSlPnlInput && sl.pnlPercentage) {
      setSlPnlInputValue(
        formatNumber(Math.abs(sl.pnlPercentage * 100), {
          displayDecimals: 0,
          isTrimTrailingZero: true,
          isUseSeparator: false,
        }),
      );
    } else if (!sl.pnlPercentage) {
      setSlPnlInputValue('');
    }
  }, [sl.pnlPercentage, isSlPnlInput]);

  const debounceTpPriceInput = useDebounceTpSlInput({
    value: tp,
    setValue: setTp,
    entryPrice,
    leverage,
    collateralUsd: collateralUsd,
    isLong,
    spreadPercentage,
  });
  const debounceSlPriceInput = useDebounceTpSlInput({
    value: sl,
    setValue: setSl,
    entryPrice,
    leverage,
    collateralUsd: collateralUsd,
    isLong,
    spreadPercentage,
  });

  const tpFormattedValue =
    tp.priceValue == '' ? '' : formatWithThousandSeparators(tp.priceValue);
  const slFormattedValue =
    sl.priceValue == '' ? '' : formatWithThousandSeparators(sl.priceValue);

  const { inputRef: tpInputRef, handleInput: handleTpInput } =
    useCursorPersistence(tpFormattedValue);
  const { inputRef: slInputRef, handleInput: handleSlInput } =
    useCursorPersistence(slFormattedValue);

  const resetEmptyOrZeroPriceOnBlur = useCallback(
    (
      priceValue: string,
      setValue: (value: React.SetStateAction<TpSlValueState>) => void,
    ) => {
      const numberValue = Number(priceValue);

      if (
        priceValue === '' ||
        !Number.isFinite(numberValue) ||
        numberValue <= 0
      ) {
        setValue(defaultTpSlValue);
      }
    },
    [],
  );

  const tpQuicks = useMemo(() => {
    // const step = Math.floor(maxTpRatios) / 5; // ex. maxTpRatios = 3.5, then floor it to be 3, always make max as integer (100%)
    // quicks = Array.from({ length: 5 }, (_, i) =>
    //   (step * (i + 1)).toFixed(3),
    // ).map(Number);
    // }

    return getTpQuickRatios(maxTpRatios).map((v) => ({
      value: v,
    }));
  }, [maxTpRatios]);

  const maxTpQuickRatio = useMemo(
    () => getTpQuickMaxRatio(maxTpRatios),
    [maxTpRatios],
  );

  const isTpQuickDisabled = useCallback(
    (pnlRate: number) => {
      if (isNil(entryPrice) || isNil(spreadPercentage)) return false;

      const targetPrice = getTakeProfitStopLossPrice({
        pnlRate,
        isTakeProfit: true,
        entryPrice,
        leverage,
        isLong,
        spreadPercentage,
      });
      const roundingMode = isLong
        ? BigNumber.ROUND_FLOOR
        : BigNumber.ROUND_CEIL;
      const priceValue = new BigNumber(targetPrice).toFixed(
        priceDisplayDecimals,
        roundingMode,
      );

      return Number(priceValue) <= 0;
    },
    [entryPrice, isLong, leverage, priceDisplayDecimals, spreadPercentage],
  );

  const getDisabledTpQuickTooltip = useCallback(
    () => 'Current leverage is too low',
    [],
  );

  const getTPSLStateByPnlRate = useCallback(
    (pnlRate: number, isTakeProfit: boolean) => {
      if (isNil(entryPrice) || isNil(spreadPercentage)) {
        console.warn(
          'entry price or spread percentage is nil',
          entryPrice,
          spreadPercentage,
        );

        return;
      }

      if (isTakeProfit) {
        return getPositiveTakeProfitState({
          requestedPnlRate: pnlRate,
          fallbackPnlRates: tpQuicks.map(({ value }) => value),
          entryPrice,
          leverage,
          isLong,
          spreadPercentage,
          priceDisplayDecimals,
          collateralUsd,
        });
      }

      const targetPrice = getTakeProfitStopLossPrice({
        pnlRate: pnlRate,
        isTakeProfit: true,
        entryPrice: entryPrice,
        leverage,
        isLong,
        spreadPercentage,
      });

      let isFloor = (isLong && isTakeProfit) || (!isLong && !isTakeProfit);

      // long tp -> floor
      // long sl -> ceil
      // short tp -> ceil
      // short sl -> floor
      const roundingMode = isFloor
        ? BigNumber.ROUND_FLOOR
        : BigNumber.ROUND_CEIL;
      const targetPriceValue = new BigNumber(targetPrice).toFixed(
        priceDisplayDecimals,
        roundingMode,
      );

      return {
        deltaPnl: getEstPnl({
          collateralUsd: collateralUsd,
          targetPercentage: pnlRate,
          isLong,
          spreadPercentage,
        }),
        pnlPercentage: pnlRate,
        isPnlPercentage: true,
        priceValue: targetPriceValue,
        isLong: isLong,
      };
    },
    [
      entryPrice,
      spreadPercentage,
      leverage,
      isLong,
      collateralUsd,
      priceDisplayDecimals,
      tpQuicks,
    ],
  );

  const tpPnlRateInput = (pnlRate: number) => {
    const tpState = getTPSLStateByPnlRate(pnlRate, true);
    if (tpState && !isSameTpSlValue(tp, tpState)) {
      setPriceUpdatePaused(true);
      setTp(tpState);
      setTimeout(() => {
        setPriceUpdatePaused(false);
      });
    }
  };

  const handleTpPnlInput = (val: string) => {
    setTpPnlInputValue(val);
    const pnlPercentage = parseFloat(val) / 100;

    if (!isNaN(pnlPercentage)) {
      tpPnlRateInput(pnlPercentage);
    }
  };

  const slPnlRateInput = (pnlRate: number) => {
    const slState = getTPSLStateByPnlRate(pnlRate, false);
    if (slState && !isSameTpSlValue(sl, slState)) {
      setPriceUpdatePaused(true);
      setSl(slState);
      setTimeout(() => {
        setPriceUpdatePaused(false);
      });
    }
  };

  const handleSlPnlInput = (val: string) => {
    setSlPnlInputValue(val);
    const floatVal = parseFloat(val);

    if (floatVal === 0 || val === '') {
      setPriceUpdatePaused(true);
      setSl(defaultTpSlValue);
      setTimeout(() => {
        setPriceUpdatePaused(false);
      });
      
return;
    }

    const pnlPercentage = -Math.abs(floatVal) / 100;

    if (!isNaN(pnlPercentage)) {
      slPnlRateInput(pnlPercentage);
    }
  };

  useEffect(() => {
    if (!priceUpdatePaused) {
      // timer = setTimeout(() => {
      if (tp.isPnlPercentage) {
        tpPnlRateInput(tp.pnlPercentage ?? 0);
      }
      if (sl.isPnlPercentage) {
        slPnlRateInput(sl.pnlPercentage ?? 0);
      }
      // }, 250);
    }

    return () => {
      // if (timer) {
      // clearTimeout(timer);
      // }
    };
  }, [getTPSLStateByPnlRate, priceUpdatePaused]);

  const slQuicks = useMemo(() => {
    return [0.1, 0.3, 0.5, 0.7].map((v) => ({
      value: -v,
    }));
  }, []);

  // set default tp
  useEffect(() => {
    if (defaultTpEnabled) {
      tpPnlRateInput(tpQuicks[tpQuicks.length - 1].value);
    }
  }, [tpQuicks, defaultTpEnabled]);

  // V2 trade-panel variant (mockups/app.html#trade): label+readout rows with
  // preset chips only — manual price/% inputs live in the Edit TP/SL editor
  if (quickPanel) {
    const slIsNone = isNil(sl.pnlPercentage) || sl.pnlPercentage === 0;
    const tpIsNone = isNil(tp.pnlPercentage) || tp.priceValue === '';
    const chipStyle = (selected: boolean, disabled?: boolean) =>
      ({
        height: '26px',
        minWidth: 0,
        borderRadius: '2px',
        border: '1px solid',
        borderColor: selected ? 'v2.long.border' : 'v2.border.default',
        background: selected ? 'v2.long.dim' : 'v2.bg.surface2',
        color: selected
          ? 'v2.long.default'
          : disabled
            ? 'v2.text.disabled'
            : 'v2.text.muted',
        fontFamily: 'Inter',
        fontSize: '11px',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.12s, color 0.12s',
        _hover: disabled
          ? {}
          : { background: selected ? 'v2.long.dim' : 'v2.bg.hover' },
      }) as const;

    return (
      <Box w="full" {...props}>
        <Flex h="28px" alignItems="center" justifyContent="space-between">
          <Text
            textStyle="v2.sectionLabel"
            color="v2.text.subtle"
          >
            {t('common.Take Profit')}
          </Text>
          <HStack gap="4px" textStyle="numbers.xsmall">
            {tpValidation.error ? (
              <Text as="span" textStyle="numbers.xsmall" color="v2.short.default">
                Invalid
              </Text>
            ) : tpIsNone ? (
              <Text as="span" textStyle="numbers.xsmall" color="v2.text.secondary">
                {t('common.None', 'None')}
              </Text>
            ) : (
              <>
                <Text as="span" textStyle="numbers.xsmall" color="v2.text.secondary">
                  {tpFormattedValue}
                </Text>
                <Text as="span" color="v2.text.subtle">
                  ·
                </Text>
                <Text as="span" textStyle="numbers.xsmall" color="v2.long.default">
                  {formatNumber(tp.deltaPnl ?? 0, {
                    prefix: '$',
                    isShowSign: true,
                    displayDecimals: 2,
                  })}
                </Text>
              </>
            )}
          </HStack>
        </Flex>
        <Grid gridAutoFlow="column" gridAutoColumns="1fr" gap="6px">
          {tpQuicks.map(({ value }) => {
            const selected =
              !tpIsNone && Boolean(tp.isPnlPercentage) && tp.pnlPercentage === value;
            const disabled = isTpQuickDisabled(value);

            return (
              <styled.button
                key={value}
                type="button"
                css={chipStyle(selected, disabled)}
                disabled={disabled}
                title={disabled ? getDisabledTpQuickTooltip() : undefined}
                onClick={() => tpPnlRateInput(value)}
                data-cy={`TpQuick-${value}`}
              >
                {formatNumber(value * 100, {
                  displayDecimals: 0,
                  isTrimTrailingZero: true,
                })}
                %
              </styled.button>
            );
          })}
        </Grid>

        <Flex h="28px" mt="6px" alignItems="center" justifyContent="space-between">
          <Text
            textStyle="v2.sectionLabel"
            color="v2.text.subtle"
          >
            {t('common.Stop Loss')}
          </Text>
          <HStack gap="4px" textStyle="numbers.xsmall">
            {slValidation.error ? (
              <Text as="span" textStyle="numbers.xsmall" color="v2.short.default">
                Invalid
              </Text>
            ) : slIsNone ? (
              <Text as="span" textStyle="numbers.xsmall" color="v2.text.secondary">
                {t('common.None', 'None')}
              </Text>
            ) : (
              <>
                <Text as="span" textStyle="numbers.xsmall" color="v2.text.secondary">
                  {slFormattedValue}
                </Text>
                <Text as="span" color="v2.text.subtle">
                  ·
                </Text>
                <Text as="span" textStyle="numbers.xsmall" color="v2.short.default">
                  {formatNumber(sl.deltaPnl ?? 0, {
                    prefix: '$',
                    isShowSign: true,
                    displayDecimals: 2,
                  })}
                </Text>
              </>
            )}
          </HStack>
        </Flex>
        <Grid gridAutoFlow="column" gridAutoColumns="1fr" gap="6px">
          <styled.button
            type="button"
            css={chipStyle(slIsNone)}
            onClick={() => {
              setPriceUpdatePaused(true);
              setSl(defaultTpSlValue);
              setTimeout(() => {
                setPriceUpdatePaused(false);
              });
            }}
            data-cy="SlQuick-none"
          >
            {t('common.None', 'None')}
          </styled.button>
          {slQuicks.map(({ value }) => {
            const selected = !slIsNone && sl.pnlPercentage === value;

            return (
              <styled.button
                key={value}
                type="button"
                css={chipStyle(selected)}
                onClick={() => slPnlRateInput(value)}
                data-cy={`SlQuick-${value}`}
              >
                −
                {formatNumber(Math.abs(value * 100), {
                  displayDecimals: 0,
                  isTrimTrailingZero: true,
                })}
                %
              </styled.button>
            );
          })}
        </Grid>
      </Box>
    );
  }

  if (!forceExpanded && !isContainerDesktop && !isExpanded) {
    return (
      <Flex
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setIsExpanded(true)}
        cursor="pointer"
        p={3}
        bg="transparent"
        w="full"
      >
        <HStack gap={8}>
          <HStack>
            <Text color="gray.text" fontSize="12px">
              {t('common.Take Profit')}
            </Text>
            <HStack gap={1}>
              <Text
                color={
                  tpValidation.valid ? 'price.priceUp' : 'primary.orangeDark'
                }
                fontSize="12px"
              >
                {tpValidation.error
                  ? 'Invalid'
                  : isNil(tp.pnlPercentage)
                  ? 'None'
                  : formatNumber(tp.pnlPercentage * 100, {
                      suffix: '%',
                      isShowSign: true,
                      displayDecimals: 0,
                    })}
              </Text>
              {!tpValidation.error && !isNil(tp.pnlPercentage) && (
                <Text color="gray.text" fontSize="12px">
                  /
                </Text>
              )}
              {!tpValidation.error && !isNil(tp.deltaPnl) && (
                <Text color="price.priceUp" fontSize="12px">
                  {formatNumber(tp.deltaPnl, {
                    prefix: '$',
                    isShowSign: true,
                    displayDecimals: 0,
                  })}
                </Text>
              )}
            </HStack>
          </HStack>

          <HStack>
            <Text color="gray.text" fontSize="12px">
              {t('common.Stop Loss')}
            </Text>
            <HStack gap={1}>
              <Text color="price.priceDown" fontSize="12px">
                {slValidation.error
                  ? 'Invalid'
                  : isNil(sl.pnlPercentage) || sl.pnlPercentage == 0
                  ? 'None'
                  : formatNumber(sl.pnlPercentage * 100, {
                      suffix: '%',
                      isShowSign: true,
                      displayDecimals: 0,
                    })}
              </Text>
              {!slValidation.error &&
                !isNil(sl.pnlPercentage) &&
                sl.pnlPercentage !== 0 && (
                  <Text color="gray.text" fontSize="12px">
                    /
                  </Text>
                )}
              {!slValidation.error &&
                !isNil(sl.deltaPnl) &&
                sl.pnlPercentage !== 0 && (
                  <Text color="price.priceDown" fontSize="12px">
                    {formatNumber(sl.deltaPnl, {
                      prefix: '$',
                      isShowSign: true,
                      displayDecimals: 0,
                    })}
                  </Text>
                )}
            </HStack>
          </HStack>
        </HStack>

        <Icon color="gray.text">
          <ArrowDown />
        </Icon>
      </Flex>
    );
  }

  return (
    <>
      <Field.Root
        {...props}
        // kill the field recipe's surface2 card + 12px padding: the editor
        // sits flush on the modal panel (mockup .mbody), and surface2 chips
        // were invisible against the recipe's same-color background
        bg="transparent"
        p="0"
        gap="0"
        data-cy="TPSLInputRoot"
      >
        <Field.Root gap="1.5" p={0} bg="transparent">
          <Flex justifyContent="space-between">
            <Flex
              h="36px"
              bg="v2.bg.surface1"
              border="1px solid"
              borderColor="v2.border.default"
              borderRadius="4px"
              alignItems="center"
              gap="0px"
              justifyContent="space-between"
              _hover={{
                borderColor: 'v2.border.strong',
              }}
              _focusWithin={{
                borderColor: 'v2.border.focus',
              }}
              transition="border-color 0.15s"
              w="full"
            >
              <Field.Label
                textStyle="v2.sectionLabel"
                color={
                  tpValidation?.valid ? 'v2.text.subtle' : 'v2.short.default'
                }
                pl="10px"
                whiteSpace="nowrap"
              >
                {t('common.Take Profit')}
              </Field.Label>
              <Field.Input
                ref={tpInputRef}
                autoComplete="off"
                placeholder="0.0"
                textStyle="numbers.small"
                color="v2.text.primary"
                w="full"
                textAlign="right"
                caretColor="v2.long.default"
                value={tpFormattedValue}
                onFocus={() => {
                  setTp({
                    ...tp,
                    isPnlPercentage: false,
                  });
                }}
                onBlur={() => {
                  resetEmptyOrZeroPriceOnBlur(tp.priceValue, setTp);
                }}
                onInput={(e) => {
                  handleTpInput(e, debounceTpPriceInput);
                }}
                padding="4px 10px"
                bg="transparent"
                border="none"
                outline="none"
                _hover={{
                  bg: 'transparent',
                }}
                _focus={{
                  bg: 'transparent',
                  boxShadow: 'none',
                  outline: 'none',
                }}
                type="text"
                inputMode="decimal"
                data-cy="TPInput"
              />
            </Flex>

            <Flex
              h="36px"
              bg="v2.bg.surface1"
              border="1px solid"
              borderColor="v2.border.default"
              borderRadius="4px"
              alignItems="center"
              gap="0px"
              justifyContent="space-between"
              _hover={{
                borderColor: 'v2.border.strong',
              }}
              _focusWithin={{
                borderColor: 'v2.border.focus',
              }}
              transition="border-color 0.15s"
              w="90px"
              ml="8px"
            >
              <Field.Input
                autoComplete="off"
                placeholder="0"
                textStyle="numbers.small"
                color="v2.text.primary"
                w="full"
                textAlign="right"
                caretColor="v2.long.default"
                value={tpPnlInputValue}
                onFocus={() => {
                  setIsTpPnlInput(true);
                  if (tp.pnlPercentage) {
                    setTpPnlInputValue(
                      formatNumber(tp.pnlPercentage * 100, {
                        displayDecimals: 0,
                        isTrimTrailingZero: true,
                        isUseSeparator: false,
                      }),
                    );
                  }
                }}
                onBlur={() => {
                  setIsTpPnlInput(false);
                }}
                onInput={(e) => {
                  handleTpPnlInput(e.currentTarget.value);
                }}
                padding="4px 2px 4px 10px"
                bg="transparent"
                border="none"
                outline="none"
                _hover={{
                  bg: 'transparent',
                }}
                _focus={{
                  bg: 'transparent',
                  boxShadow: 'none',
                  outline: 'none',
                }}
                type="text"
                inputMode="decimal"
                data-cy="TPPnlInput"
              />
              <Text
                textStyle="v2.meta"
                color="v2.text.muted"
                pr="10px"
              >
                %
              </Text>
            </Flex>
          </Flex>

          <RadioButtonGroup.Root
            value={`${tp.pnlPercentage}`}
            onValueChange={(v) => {
              const pnlRate = parseFloat(v.value);
              if (isTpQuickDisabled(pnlRate)) return;

              tpPnlRateInput(pnlRate);
            }}
            gap="6px"
            w="full"
            size="xsm"
          >
            {tpQuicks.map((option, id) => {
              const disabled = isTpQuickDisabled(option.value);
              const item = (
                <RadioButtonGroup.Item
                  _checked={{
                    color: 'v2.long.default',
                    background: 'v2.long.dim',
                    borderColor: 'v2.long.border',
                  }}
                  _hover={{
                    background: disabled ? 'v2.bg.surface2' : 'v2.bg.hover',
                  }}
                  disabled={disabled}
                  h="26px"
                  borderRadius="2px"
                  background="v2.bg.surface2"
                  borderColor="v2.border.default"
                  color={disabled ? 'v2.text.disabled' : 'v2.text.muted'}
                  fontFamily="Inter"
                  fontSize="11px"
                  fontWeight={500}
                  opacity={disabled ? 0.55 : 1}
                  cursor={disabled ? 'not-allowed' : 'pointer'}
                  flex="1"
                  minW="0"
                  key={id}
                  value={`${option.value}`}
                >
                  <RadioButtonGroup.ItemControl />
                  <RadioButtonGroup.ItemText>
                    {option.value * 100}%
                  </RadioButtonGroup.ItemText>
                  <RadioButtonGroup.ItemHiddenInput />
                </RadioButtonGroup.Item>
              );

              if (!disabled) return item;

              return (
                <TextTooltip
                  key={id}
                  content={
                    <Text textStyle="text.small" color="white" whiteSpace="pre-line">
                      {getDisabledTpQuickTooltip()}
                    </Text>
                  }
                  contentCss={{ maxW: '240px' }}
                >
                  {item}
                </TextTooltip>
              );
            })}
          </RadioButtonGroup.Root>

          <Flex
            minH="24px"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            px="1"
          >
            <TextTooltip
              content={
                <Text textStyle="text.small" color="white">
                  Estimated profit before fees
                </Text>
              }
            >
              <Text
                textStyle="v2.meta"
                color="v2.text.muted"
                cursor="help"
                textDecoration="none"
              >
                Profit:
              </Text>
            </TextTooltip>
            <Field.Label textStyle="v2.meta" minW={0}>
              <Flex minW={0} alignItems="center" gap={1}>
                <Text
                  color={
                    tpValidation.valid ? 'v2.long.default' : 'v2.short.default'
                  }
                  textAlign="right"
                  whiteSpace="normal"
                  overflowWrap="anywhere"
                >
                  {tpValidation.error &&
                    getTakeProfitPriceErrorMessage({
                      t,
                      error: tpValidation.error,
                      maxTpRatios: maxTpQuickRatio,
                    })}
                  {/* {tpValidation.error
                    ? getTakeProfitPriceErrorMessage({
                        t,
                        error: tpValidation.error,
                        maxTpRatios,
                      })
                    : isNil(tp.pnlPercentage)
                    ? ''
                    : formatNumber(tp.pnlPercentage * 100, {
                        suffix: '%',
                        isShowSign: true,
                        displayDecimals: 0,
                      })} */}
                </Text>
                {/* {!tpValidation.error && <Text>/</Text>} */}
                <Text
                  textStyle="numbers.small"
                  color={isNil(tp.deltaPnl) ? 'v2.text.subtle' : 'v2.long.default'}
                >
                  {tpValidation.error
                    ? ''
                    : isNil(tp.deltaPnl)
                    ? '—'
                    : formatNumber(tp.deltaPnl, {
                        prefix: '$',
                        isShowSign: true,
                        displayDecimals: 2,
                      })}
                </Text>
              </Flex>
            </Field.Label>
          </Flex>
        </Field.Root>

        <Divider my={2} borderColor="v2.border.hairline" />

        <Field.Root gap="1.5" p={0} bg="transparent">
          <Flex justifyContent="space-between">
            <Flex
              h="36px"
              bg="v2.bg.surface1"
              border="1px solid"
              borderColor="v2.border.default"
              borderRadius="4px"
              alignItems="center"
              gap="0px"
              justifyContent="space-between"
              _hover={{
                borderColor: 'v2.border.strong',
              }}
              _focusWithin={{
                borderColor: 'v2.border.focus',
              }}
              transition="border-color 0.15s"
              w="full"
            >
              <Field.Label
                textStyle="v2.sectionLabel"
                color={
                  slValidation?.valid ? 'v2.text.subtle' : 'v2.short.default'
                }
                pl="10px"
                whiteSpace="nowrap"
              >
                {t('common.Stop Loss')}
              </Field.Label>
              <Field.Input
                ref={slInputRef}
                autoComplete="off"
                placeholder="None"
                textStyle="numbers.small"
                color="v2.text.primary"
                w="full"
                textAlign="right"
                caretColor="v2.short.default"
                value={slFormattedValue}
                onFocus={() => {
                  setSl({
                    ...sl,
                    isPnlPercentage: false,
                  });
                }}
                onBlur={() => {
                  resetEmptyOrZeroPriceOnBlur(sl.priceValue, setSl);
                }}
                onInput={(e) => {
                  handleSlInput(e, debounceSlPriceInput);
                }}
                padding="4px 10px"
                bg="transparent"
                border="none"
                outline="none"
                _hover={{
                  bg: 'transparent',
                }}
                _focus={{
                  bg: 'transparent',
                  boxShadow: 'none',
                  outline: 'none',
                }}
                type="text"
                inputMode="decimal"
                data-cy="SLInput"
              />
            </Flex>

            {sl.pnlPercentage && sl.pnlPercentage !== 0 ? (
              <Flex
                h="36px"
                bg="v2.bg.surface1"
                border="1px solid"
                borderColor="v2.border.default"
                borderRadius="4px"
                alignItems="center"
                gap="0px"
                justifyContent="space-between"
                _hover={{
                  borderColor: 'v2.border.strong',
                }}
                _focusWithin={{
                  borderColor: 'v2.border.focus',
                }}
                transition="border-color 0.15s"
                w="90px"
                ml="8px"
              >
                <Text
                  textStyle="v2.meta"
                  color="v2.text.muted"
                  pl="10px"
                  pr="2px"
                >
                  -
                </Text>
                <Field.Input
                  autoComplete="off"
                  placeholder="0"
                  textStyle="numbers.small"
                  color="v2.text.primary"
                  w="full"
                  textAlign="right"
                  caretColor="v2.short.default"
                  value={slPnlInputValue}
                  onFocus={() => {
                    setIsSlPnlInput(true);
                  }}
                  onBlur={() => {
                    setIsSlPnlInput(false);
                  }}
                  autoFocus={isSlPnlInput}
                  onInput={(e) => {
                    handleSlPnlInput(e.currentTarget.value);
                  }}
                  padding="4px 2px"
                  bg="transparent"
                  border="none"
                  outline="none"
                  _hover={{
                    bg: 'transparent',
                  }}
                  _focus={{
                    bg: 'transparent',
                    boxShadow: 'none',
                    outline: 'none',
                  }}
                  type="text"
                  inputMode="decimal"
                  data-cy="SLPnlInput"
                />
                <Text
                  textStyle="v2.meta"
                  color="v2.text.muted"
                  pr="10px"
                >
                  %
                </Text>
              </Flex>
            ) : null}
          </Flex>
          <RadioButtonGroup.Root
            value={`${sl.pnlPercentage ?? 0}`}
            onValueChange={(v) => {
              if (v.value == '0') {
                setPriceUpdatePaused(true);
                setSl(defaultTpSlValue);
                // Also clear the PnL input value
                setSlPnlInputValue('');
                setTimeout(() => {
                  setPriceUpdatePaused(false);
                });
              } else {
                // When clicking a quick option, we are not manually typing in the PnL input
                setIsSlPnlInput(false);
                slPnlRateInput(parseFloat(v.value));
              }
            }}
            gap="6px"
            w="full"
            size="xsm"
          >
            <RadioButtonGroup.Item
              h="26px"
              borderRadius="2px"
              background="v2.bg.surface2"
              borderColor="v2.border.default"
              color="v2.text.muted"
              fontFamily="Inter"
              fontSize="11px"
              fontWeight={500}
              flex="1"
              minW="0"
              key="sl-0"
              data-cy="SLNone"
              value="0"
              _hover={{
                background: 'v2.bg.hover',
              }}
              _checked={{
                color: 'v2.long.default',
                background: 'v2.long.dim',
                borderColor: 'v2.long.border',
              }}
            >
              <RadioButtonGroup.ItemControl />
              <RadioButtonGroup.ItemText>None</RadioButtonGroup.ItemText>
              <RadioButtonGroup.ItemHiddenInput />
            </RadioButtonGroup.Item>
            {slQuicks.map((option, id) => (
              <RadioButtonGroup.Item
                _checked={{
                  color: 'v2.long.default',
                  background: 'v2.long.dim',
                  borderColor: 'v2.long.border',
                }}
                _hover={{
                  background: 'v2.bg.hover',
                }}
                h="26px"
                borderRadius="2px"
                background="v2.bg.surface2"
                borderColor="v2.border.default"
                color="v2.text.muted"
                fontFamily="Inter"
                fontSize="11px"
                fontWeight={500}
                flex="1"
                minW="0"
                key={id}
                value={`${option.value}`}
              >
                <RadioButtonGroup.ItemControl />
                <RadioButtonGroup.ItemText>
                  {option.value * 100}%
                </RadioButtonGroup.ItemText>
                <RadioButtonGroup.ItemHiddenInput />
              </RadioButtonGroup.Item>
            ))}
          </RadioButtonGroup.Root>
          <Flex
            minH="24px"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            px="1"
            display={
              sl.pnlPercentage && sl.pnlPercentage !== 0 ? 'flex' : 'none'
            }
          >
            <TextTooltip
              content={
                <Text textStyle="text.small" color="white">
                  Estimated loss before fees
                </Text>
              }
            >
              <Text
                textStyle="v2.meta"
                color="v2.text.muted"
                cursor="help"
                textDecoration="none"
              >
                Loss:
              </Text>
            </TextTooltip>

            <Field.Label
              alignItems="center"
              display="flex"
              textStyle="v2.meta"
              minW={0}
            >
              <Flex gap={1} minW={0} alignItems="center">
                <Text
                  color="v2.short.default"
                  textAlign="right"
                  whiteSpace="normal"
                  overflowWrap="anywhere"
                >
                  {slValidation.error &&
                    getStopLossPriceErrorMessage(t, slValidation.error)}
                  {/* {slValidation.error
                    ? getStopLossPriceErrorMessage(t, slValidation.error)
                    : isNil(sl.pnlPercentage) || sl.pnlPercentage == 0
                    ? ''
                    : formatNumber(sl.pnlPercentage * 100, {
                        suffix: '%',
                        isShowSign: true,
                        displayDecimals: 0,
                      })} */}
                </Text>
                {/* <Text>
                  {slValidation.error
                    ? ''
                    : isNil(sl.pnlPercentage) || sl.pnlPercentage == 0
                    ? ''
                    : '/'}
                </Text> */}
                <Text
                  textStyle="numbers.small"
                  color={isNil(sl.deltaPnl) ? 'v2.text.subtle' : 'v2.short.default'}
                >
                  {slValidation.error
                    ? ''
                    : isNil(sl.deltaPnl)
                    ? '—'
                    : formatNumber(sl.deltaPnl, {
                        prefix: '$',
                        isShowSign: true,
                        displayDecimals: 2,
                      })}
                </Text>
              </Flex>
            </Field.Label>
          </Flex>
        </Field.Root>

        {/* <Portal container={errPortalRef}>
          <Error msg={errorMessage || ''} />
        </Portal> */}
      </Field.Root>
    </>
  );
};
